import { useState } from "react";
import { useAdminPlans, useUpdatePlanFeature } from "@/hooks/use-admin-plans";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save } from "lucide-react";

interface PendingChange {
  feature_id: string;
  enabled: boolean;
  limit_value: number | null;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);
}

export default function AdminPlans() {
  const { data: plans, isLoading } = useAdminPlans();
  const updateFeature = useUpdatePlanFeature();
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());

  const setChange = (featureId: string, enabled: boolean, limitValue: number | null) => {
    const next = new Map(pendingChanges);
    next.set(featureId, { feature_id: featureId, enabled, limit_value: limitValue });
    setPendingChanges(next);
  };

  const getFeatureState = (feature: { id: string; enabled: boolean; limit_value: number | null }) => {
    const pending = pendingChanges.get(feature.id);
    if (pending) return { enabled: pending.enabled, limit_value: pending.limit_value };
    return { enabled: feature.enabled, limit_value: feature.limit_value };
  };

  const handleSaveAll = async () => {
    const changes = Array.from(pendingChanges.values());
    for (const change of changes) {
      await updateFeature.mutateAsync(change);
    }
    setPendingChanges(new Map());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Planos</h2>
          <p className="text-muted-foreground">
            Gerencie os planos e suas features
          </p>
        </div>
        {pendingChanges.size > 0 && (
          <Button onClick={handleSaveAll} disabled={updateFeature.isPending} className="w-full sm:w-auto">
            {updateFeature.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            <span className="sm:hidden">Salvar ({pendingChanges.size})</span>
            <span className="hidden sm:inline">Salvar {pendingChanges.size} alteracao(oes)</span>
          </Button>
        )}
      </div>

      {(plans ?? []).map((plan) => (
        <Card key={plan.id}>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={plan.is_active ? "default" : "secondary"}>
                  {plan.is_active ? "Ativo" : "Inativo"}
                </Badge>
                <span className="text-lg font-bold whitespace-nowrap">
                  {formatCurrency(plan.price_cents)}
                  {plan.price_cents > 0 && <span className="text-sm font-normal text-muted-foreground">/mes</span>}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="w-20 sm:w-24 text-center">Ativo</TableHead>
                    <TableHead className="w-24 sm:w-32 text-center">Limite</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.features.map((feature) => {
                    const state = getFeatureState(feature);
                    return (
                      <TableRow key={feature.id}>
                        <TableCell className="font-mono text-xs sm:text-sm break-all">
                          {feature.feature_key}
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={state.enabled}
                            onCheckedChange={(checked) =>
                              setChange(feature.id, checked, state.limit_value)
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            value={state.limit_value ?? ""}
                            placeholder="--"
                            className="w-20 sm:w-28 mx-auto text-center"
                            onChange={(e) => {
                              const val = e.target.value;
                              const limit = val === "" ? null : parseInt(val, 10);
                              setChange(feature.id, state.enabled, limit);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {plan.features.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                        Nenhuma feature cadastrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
